import { useNavigate } from 'react-router-dom';
import { useB3Lang } from '@b3/lang';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';

import { currencyFormat } from '@/utils';

import OrderStatus from './components/OrderStatus';
import OrderCancellationStatus from './components/OrderCancellationStatus';

interface ListItem {
  orderId: string;
  firstName: string;
  lastName: string;
  poNumber?: string;
  status: string;
  statusCode?: string;
  cancellationStatus: string;
  cancellationStatusCode?: string;
  totalIncTax: string;
  createdAt: string;

  orderNumber: string;
  orderTotal: {
    amount: number;
  }
  company: string;

}

export interface OrderItemCardProps {
  allTotal: number;
  filterData: any;
  index?: number;
  item: ListItem;
  isCompanyOrder: boolean;
}

const Flex = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  '&.between-flex': {
    justifyContent: 'space-between',
  },
}));

export function OrderItemCard({
  item,
  allTotal,
  filterData,
  index = 0,
  isCompanyOrder,
}: OrderItemCardProps) {
  const b3Lang = useB3Lang();
  const theme = useTheme();
  const navigate = useNavigate();

  const goToDetail = (item: ListItem) => {
    navigate(`/orderDetail/${item.orderNumber}`, {
      state: {
        currentIndex: index || 0,
        searchParams: filterData,
        totalCount: allTotal,
        isCompanyOrder,
      },
    });
  };

  return (
    <Card key={item.orderId}>
      <CardContent
        sx={{
          color: 'rgba(0, 0, 0, 0.6)',
        }}
        onClick={() => goToDetail(item)}
      >
        <Flex className="between-flex">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: 'rgba(0, 0, 0, 0.87)',
              }}
            >
              {`# ${item.orderNumber}`}
            </Typography>
            <Typography
              sx={{
                ml: 1,
              }}
              variant="body2"
            >
              {item.poNumber ? item.poNumber : '–'}
            </Typography>
          </Box>
          {item.statusCode && (
            <Box>
              <OrderStatus code={item.statusCode} text={b3Lang(item.status)} />
            </Box>
          )}
          {item?.cancellationStatusCode && (
            <Box>
              <OrderCancellationStatus code={item.cancellationStatusCode} text={b3Lang(item.cancellationStatus)} />
            </Box>
          )}
        </Flex>

        <Typography
          variant="h6"
          sx={{
            marginBottom: theme.spacing(2.5),
            mt: theme.spacing(1.5),
            minHeight: '1.43em',
          }}
        >
          {currencyFormat(item.orderTotal.amount)}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'normal',
              marginRight: theme.spacing(2),
            }}
          >
          {`${item.company}`}
          </Typography>
          <Typography>{`${item.createdAt}`}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
